import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import ScholarAvatar from "@/components/ScholarAvatar";
import VerifiedBadge from "@/components/VerifiedBadge";
import BrandMark from "@/components/BrandMark";
import Seo from "@/components/Seo";
import { CalendarDays, MapPin, Users, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MeetupPreview() {
  const { id } = useParams();
  const [ev, setEv] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/events/${id}`)
      .then((r) => setEv(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "notfound" : "error"));
  }, [id]);

  return (
    <div className="min-h-screen bg-[color:var(--ayur-cream)]">
      {ev && (
        <Seo
          title={ev.title}
          description={`${ev.reason || "Meet-up"} · ${new Date(ev.date).toLocaleString()} · ${ev.location} — ${(ev.description || "").slice(0, 100)}`}
          path={`/meetup/${id}`}
          type="event"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Event",
            "name": ev.title,
            "description": ev.description,
            "startDate": ev.date,
            "endDate": ev.end_date || ev.date,
            "eventAttendanceMode": ev.mode === "Online"
              ? "https://schema.org/OnlineEventAttendanceMode"
              : ev.mode === "Hybrid"
              ? "https://schema.org/MixedEventAttendanceMode"
              : "https://schema.org/OfflineEventAttendanceMode",
            "eventStatus": "https://schema.org/EventScheduled",
            "location": ev.mode === "Online"
              ? { "@type": "VirtualLocation", "url": ev.meeting_link || `https://vaidgram.in/meetup/${id}` }
              : { "@type": "Place", "name": ev.location, "address": ev.location },
            "organizer": { "@type": "Person", "name": ev.host?.full_name || "VaidGram scholar" },
            "url": `https://vaidgram.in/meetup/${id}`
          }}
        />
      )}
      <header className="border-b border-[color:var(--ayur-border)] bg-white/70 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[color:var(--ayur-emerald)]">
            <BrandMark size={40} />
            <span className="font-serif-display text-2xl font-semibold">VaidGram</span>
          </Link>
          <Link to="/signup">
            <Button className="bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white rounded-full">
              Join VaidGram
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {error === "notfound" && (
          <div className="paper-card p-12 text-center">
            <h1 className="font-serif-display text-3xl text-[color:var(--ayur-emerald)]">Meet-up not found</h1>
            <p className="text-[color:var(--ayur-mute)] mt-2">It may have been cancelled or removed.</p>
          </div>
        )}
        {!error && !ev && (
          <div className="paper-card p-12 text-center text-[color:var(--ayur-mute)]">Loading…</div>
        )}
        {ev && (
          <>
            <article data-testid="public-meetup-card" className="paper-card p-6 sm:p-8">
              <div className="flex items-center gap-1.5 flex-wrap">
                {ev.reason && <span className="pill">{ev.reason}</span>}
                {ev.mode && <span className="pill">{ev.mode}</span>}
                {ev.specialty && <span className="pill">{ev.specialty}</span>}
              </div>
              <h1 className="font-serif-display text-4xl mt-3 text-[color:var(--ayur-emerald)]">{ev.title}</h1>
              <p className="mt-3 text-[color:var(--ayur-ink)] leading-relaxed whitespace-pre-wrap">{ev.description}</p>
              <div className="mt-5 text-sm text-[color:var(--ayur-ink-2)] space-y-2">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" />
                  {new Date(ev.date).toLocaleString()}
                  {ev.end_date ? ` → ${new Date(ev.end_date).toLocaleString()}` : ""}
                </div>
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4" />{ev.location}</div>
                <div className="flex items-center gap-2"><Users className="w-4 h-4" />{ev.rsvp_count} attending</div>
              </div>
              <div className="mt-5 pt-4 border-t border-[color:var(--ayur-border)] flex items-center gap-3">
                <ScholarAvatar user={ev.host} size={40} />
                <div className="text-sm">
                  <div className="text-[color:var(--ayur-mute)]">Hosted by</div>
                  <div className="font-semibold inline-flex items-center gap-1">
                    {ev.host?.full_name}{ev.host?.verified && <VerifiedBadge size={14} />}
                  </div>
                </div>
              </div>
            </article>

            <div data-testid="preview-join-cta" className="mt-8 p-6 sm:p-8 rounded-2xl text-center bg-[color:var(--ayur-emerald)] text-white shadow-lg">
              <div className="label-eyebrow text-[color:var(--ayur-gold)]">RSVP with your scholar profile</div>
              <h2 className="font-serif-display text-3xl mt-2 text-white">Sign in to RSVP</h2>
              <p className="text-white/85 mt-2">Join VaidGram — BAMS/MD/MS scholars only — to say you're coming and meet the host.</p>
              <Link to="/signup" className="inline-block mt-5">
                <Button data-testid="preview-join-cta-btn" className="bg-[color:var(--ayur-gold)] hover:bg-[color:var(--ayur-gold)]/90 text-white rounded-full px-8">
                  Join VaidGram <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <div className="text-xs text-white/70 mt-3">
                Already a scholar? <Link to="/login" className="underline">Log in</Link>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
